import { useState } from 'react';
import { Copy } from 'lucide-react';
import { Button } from './ui/button';
import { SheetModal } from './SheetModal';
import {
  CarePhase,
  IRR_SUSPECT_EXPLANATION_COMBINED,
  IRR_SUSPECT_MESSAGE,
  formatCurrency,
  formatPercent,
  fundingPolicyTypeDefs,
} from '@shared';

interface IrrCashflowDebugModalProps {
  open: boolean;
  onClose: () => void;
  client: Client;
}

function formatCashflowForCopy(cashflows: CashFlow[]): string {
  return cashflows.map(cf => `${cf.year}-01-01\t${cf.amount}`).join('\n');
}

function mergeCashflowsByDate(cashflowGroups: CashFlow[][]): string {
  const cashflowMap = new Map<number, number>();

  cashflowGroups.forEach(group => {
    group.forEach(cf => {
      cashflowMap.set(cf.year, (cashflowMap.get(cf.year) ?? 0) + cf.amount);
    });
  });

  return Array.from(cashflowMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, amount]) => `${year}-01-01\t${amount}`)
    .join('\n');
}

function CopyButton({ onClick, copied }: { onClick: () => void; copied: boolean }) {
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={onClick}
      className="h-7 px-2 text-xs font-medium"
    >
      <Copy className="!size-3" />
      {copied ? 'Copied!' : 'Copy Cashflow'}
    </Button>
  );
}

function getSelfFundingName(name?: string | null) {
  const defaultName = 'Self-funding';
  return !name || name === defaultName ? defaultName : `${defaultName} (${name})`;
}

export function IrrCashflowDebugModal({ open, onClose, client }: IrrCashflowDebugModalProps) {
  const { fundingSources, multipleFundingSources } = client;
  const [copiedId, setCopiedId] = useState<string | null>(null);

  async function handleCopy(text: string, id: string) {
    await navigator.clipboard.writeText(text);

    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  return (
    <SheetModal
      open={open}
      onClose={onClose}
      title="IRR Cashflow Analysis"
      subTitle="Detailed cashflow breakdown for IRR calculations"
      modal={true}
      className="!min-w-[700px]"
    >
      <div className="flex flex-col gap-6 py-4">
        {/* Combined Summary */}
        <div className="border-primary-200 bg-primary-50 rounded-xl border p-4">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Overall Summary</h3>
            <CopyButton
              onClick={() => {
                const groups: CashFlow[][] = [];

                Object.values(multipleFundingSources?.ltcPolicy || {}).forEach(policy => {
                  if (policy?.investmentCashFlows && policy?.returnCashFlows) {
                    groups.push(
                      policy.investmentCashFlows.map(cf => ({ ...cf, amount: -cf.amount })),
                    );
                    groups.push(policy.returnCashFlows);
                  }
                });

                Object.values(multipleFundingSources?.selfFunding || {}).forEach(selfFunding => {
                  if (
                    selfFunding?.hasSelfFunding &&
                    selfFunding?.investmentCashFlows &&
                    selfFunding?.returnCashFlows
                  ) {
                    groups.push(
                      selfFunding.investmentCashFlows.map(cf => ({
                        ...cf,
                        amount: -cf.amount,
                      })),
                    );
                    groups.push(selfFunding.returnCashFlows);
                  }
                });

                if (
                  fundingSources?.annuity?.hasAnnuity &&
                  fundingSources.annuity.investmentCashFlows &&
                  fundingSources.annuity.returnCashFlows
                ) {
                  groups.push(
                    fundingSources.annuity.investmentCashFlows.map(cf => ({
                      ...cf,
                      amount: -cf.amount,
                    })),
                  );
                  groups.push(fundingSources.annuity.returnCashFlows);
                }

                handleCopy(mergeCashflowsByDate(groups), 'combined');
              }}
              copied={copiedId === 'combined'}
            />
          </div>
          <dl className="grid grid-cols-2 gap-4 text-gray-900 md:grid-cols-3">
            <SummaryItem
              label="Internal Rate of Return"
              value={
                fundingSources.combinedIsIrrSuspect
                  ? IRR_SUSPECT_MESSAGE
                  : formatPercent(fundingSources.combinedProjectedInternalRateOfReturn, 2)
              }
              note={
                fundingSources.combinedIsIrrSuspect ? IRR_SUSPECT_EXPLANATION_COMBINED : undefined
              }
            />
            <SummaryItem
              label="Compound Annual Growth"
              value={formatPercent(fundingSources.combinedProjectedCompoundAnnualGrowthRate, 2)}
            />
            <SummaryItem
              label="Return on Investment"
              value={formatPercent(fundingSources.combinedProjectedReturnOnInvestment, 2)}
            />
          </dl>
        </div>

        {/* Policy Details */}
        {Object.entries(multipleFundingSources?.ltcPolicy || {}).map(
          ([fundingSourceId, policy]) => {
            if (!policy?.investmentCashFlows && !policy?.returnCashFlows) return null;

            const policyTypeName =
              policy?.policyType && fundingPolicyTypeDefs[policy.policyType]
                ? fundingPolicyTypeDefs[policy.policyType].label
                : 'Policy';

            return (
              <div
                key={fundingSourceId}
                className="border-primary-200 rounded-xl border p-4"
              >
                <div className="mb-4 flex items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{policyTypeName}</h3>
                    {policy.customPolicyId && (
                      <span className="text-sm text-gray-700">({policy.customPolicyId})</span>
                    )}
                  </div>
                  <CopyButton
                    onClick={() => {
                      const investments = policy.investmentCashFlows
                        ? formatCashflowForCopy(
                            policy.investmentCashFlows.map(cf => ({ ...cf, amount: -cf.amount })),
                          )
                        : '';
                      const returns = policy.returnCashFlows
                        ? formatCashflowForCopy(policy.returnCashFlows)
                        : '';
                      const combined = [investments, returns].filter(Boolean).join('\n');
                      handleCopy(combined, `policy-${fundingSourceId}`);
                    }}
                    copied={copiedId === `policy-${fundingSourceId}`}
                  />
                </div>

                <div className="space-y-3">
                  <dl className="border-primary-200 grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
                    <MetricRow
                      label="IRR"
                      value={
                        policy.isIrrSuspect
                          ? IRR_SUSPECT_MESSAGE
                          : formatPercent(policy.policyProjectedInternalRateOfReturn, 2)
                      }
                    />
                    <MetricRow
                      label="CAGR"
                      value={formatPercent(policy.policyProjectedCompoundAnnualGrowthRate, 2)}
                    />
                    <MetricRow
                      label="Total Paid"
                      value={formatCurrency(policy.policyTotalCost ?? 0)}
                    />
                    <MetricRow
                      label="Total Benefits"
                      value={formatCurrency(policy.policyTotalValue ?? 0)}
                    />
                  </dl>
                </div>
              </div>
            );
          },
        )}

        {/* Self-Funding */}
        {Object.entries(multipleFundingSources?.selfFunding || {}).map(
          ([fundingSourceId, selfFunding]) => {
            if (!selfFunding?.hasSelfFunding) return null;

            return (
              <div
                key={fundingSourceId}
                className="border-primary-200 rounded-xl border p-4"
              >
                <div className="mb-4 flex items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {getSelfFundingName(selfFunding?.selfFundingName)}
                    </h3>
                  </div>
                  <CopyButton
                    onClick={() => {
                      const investments = selfFunding.investmentCashFlows
                        ? formatCashflowForCopy(
                            selfFunding.investmentCashFlows.map(cf => ({
                              ...cf,
                              amount: -cf.amount,
                            })),
                          )
                        : '';
                      const returns = selfFunding.returnCashFlows
                        ? formatCashflowForCopy(selfFunding.returnCashFlows)
                        : '';
                      const combined = [investments, returns].filter(Boolean).join('\n');
                      handleCopy(combined, `self-funding-${fundingSourceId}`);
                    }}
                    copied={copiedId === `self-funding-${fundingSourceId}`}
                  />
                </div>
                <div className="space-y-3">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
                    <MetricRow
                      label="IRR"
                      value={
                        selfFunding.isIrrSuspect
                          ? IRR_SUSPECT_MESSAGE
                          : formatPercent(selfFunding.selfFundingProjectedInternalRateOfReturn, 2)
                      }
                    />
                    <MetricRow
                      label="Annual Return Rate"
                      value={formatPercent(selfFunding.annualRateOfReturn, 2)}
                    />
                    <MetricRow
                      label="Total Invested"
                      value={formatCurrency(
                        selfFunding.investmentCashFlows?.reduce((sum, cf) => sum + cf.amount, 0) ??
                          0,
                      )}
                    />
                    <MetricRow
                      label="Total Returns"
                      value={formatCurrency(
                        selfFunding.returnCashFlows?.reduce((sum, cf) => sum + cf.amount, 0) ?? 0,
                      )}
                    />
                    <MetricRow
                      label="Investment Period"
                      value={
                        selfFunding.investmentCashFlows &&
                        selfFunding.investmentCashFlows.length > 0
                          ? `${Math.min(
                              ...selfFunding.investmentCashFlows.map(cf => cf.year),
                            )} - ${Math.max(
                              ...selfFunding.investmentCashFlows.map(cf => cf.year),
                            )}`
                          : `${selfFunding.contributionStartYear} - ${selfFunding.contributionEndYear}`
                      }
                    />
                    <MetricRow
                      label="Return Period"
                      value={
                        selfFunding.returnCashFlows && selfFunding.returnCashFlows.length > 0
                          ? `${Math.min(
                              ...selfFunding.returnCashFlows.map(cf => cf.year),
                            )} - ${Math.max(
                              ...selfFunding.returnCashFlows.map(cf => cf.year),
                            )}`
                          : 'N/A'
                      }
                    />
                  </dl>
                </div>
              </div>
            );
          },
        )}

        {/* Annuity */}
        {fundingSources?.annuity?.hasAnnuity && (
          <div className="border-primary-200 rounded-xl border p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Annuity</h3>
              <CopyButton
                onClick={() => {
                  const annuity = fundingSources.annuity;
                  if (!annuity) return;

                  const investments = annuity.investmentCashFlows
                    ? formatCashflowForCopy(
                        annuity.investmentCashFlows.map(cf => ({ ...cf, amount: -cf.amount })),
                      )
                    : '';
                  const returns = annuity.returnCashFlows
                    ? formatCashflowForCopy(annuity.returnCashFlows)
                    : '';
                  const combined = [investments, returns].filter(Boolean).join('\n');
                  handleCopy(combined, 'annuity');
                }}
                copied={copiedId === 'annuity'}
              />
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
              <MetricRow
                label="IRR"
                value={
                  fundingSources.annuity.isIrrSuspect
                    ? IRR_SUSPECT_MESSAGE
                    : formatPercent(
                        fundingSources.annuity.annuityProjectedInternalRateOfReturn,
                        2,
                      )
                }
              />
              <MetricRow
                label="Total Paid"
                value={formatCurrency(fundingSources.annuity.annuityTotalCost ?? 0)}
              />
              <MetricRow
                label="Total Payouts"
                value={formatCurrency(fundingSources.annuity.annuityTotalValue ?? 0)}
              />
              <MetricRow
                label="Purchase Price"
                value={formatCurrency(fundingSources.annuity.annuityPurchasePrice ?? 0)}
              />
              <MetricRow
                label="Annual Payout"
                value={formatCurrency(fundingSources.annuity.annuityAnnualPayOut ?? 0)}
              />
              <MetricRow
                label="LTC Payout"
                value={formatCurrency(fundingSources.annuity.annuityAnnualPayoutForLtc ?? 0)}
              />
            </dl>
          </div>
        )}

        {/* Methodology Note */}
        <div className="border-t pt-4 text-sm text-gray-900">
          <p>
            IRR (Internal Rate of Return) represents the annualized effective rate of return,
            calculated using the XIRR method with Newton-Raphson convergence. Investment cashflows
            are negative (money paid), while return cashflows are positive (benefits received).
          </p>
        </div>
      </div>
    </SheetModal>
  );
}

function SummaryItem({
  label,
  value,
  note,
}: {
  label: string;
  value: string | undefined;
  note?: string;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-gray-700">{label}</dt>
      <dd className="mt-1 text-base font-semibold">{value ?? 'N/A'}</dd>
      {note && <p className="mt-1 text-xs text-gray-500">{note}</p>}
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <>
      <dt className="font-medium text-gray-900">{label}:</dt>
      <dd className="text-primary-dark font-semibold">{value ?? 'N/A'}</dd>
    </>
  );
}
